import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import {
  useDebugMode,
  useDebugStats,
  useSetManualDebug,
  useClearTraces,
  useClearLogs,
  useDebugPairs,
} from '../../../api/useUnwindDebug';
import { useUnwindDebugSocket } from '../../../hooks/useUnwindDebugSocket';
import {
  TraceLevel,
  TraceCategory,
  TraceQuery,
  LogQuery,
} from '../../../types/unwind-debug.types';
import { MenuItem, Select } from '@mui/material';
import { DebugFilters, DebugFiltersValue, rangeToSinceIso } from './DebugFilters';
import { TracesView } from './TracesView';
import { LogsView } from './LogsView';
import { ClearDataDialog } from './ClearDataDialog';

const DEFAULT_FILTERS: DebugFiltersValue = {
  pairId: null,
  ruleId: '',
  level: [],
  category: [],
  q: '',
  range: '1h',
};

export function DebugPanel() {
  const [tab, setTab] = useState<'traces' | 'logs'>('traces');
  const [filters, setFilters] = useState<DebugFiltersValue>(DEFAULT_FILTERS);
  const [liveTail, setLiveTail] = useState(true);
  const [clearOpen, setClearOpen] = useState<{ scope: 'pair' | 'all' } | null>(null);

  const { data: pairs = [] } = useDebugPairs();
  const selectedPairName = filters.pairId
    ? pairs.find((p) => p.pairId === filters.pairId)?.pairName ?? null
    : null;

  const { data: stats } = useDebugStats();
  const { data: debugModeList = [] } = useDebugMode();
  const setManualDebug = useSetManualDebug();
  const clearTraces = useClearTraces();
  const clearLogs = useClearLogs();

  const live = useUnwindDebugSocket({
    pairId: filters.pairId ?? undefined,
    enabled: liveTail,
  });

  const since = useMemo(() => rangeToSinceIso(filters.range), [filters.range]);

  const traceQuery: TraceQuery = {
    pairId: filters.pairId ?? undefined,
    ruleId: filters.ruleId || undefined,
    level: filters.level.length ? (filters.level as TraceLevel[]) : undefined,
    category: filters.category.length ? (filters.category as TraceCategory[]) : undefined,
    q: filters.q || undefined,
    since,
    limit: 200,
  };

  const logQuery: LogQuery = {
    pairId: filters.pairId ?? undefined,
    ruleId: filters.ruleId || undefined,
    level: filters.level.length ? (filters.level as TraceLevel[]) : undefined,
    q: filters.q || undefined,
    since,
    limit: 200,
  };

  // Compute the effective level + source for the selected pair. Manual
  // override beats auto pin (matches backend resolution).
  const pairEntries = filters.pairId
    ? debugModeList.filter((d) => d.pairId === filters.pairId)
    : [];
  const manualEntry = pairEntries.find((e) => e.source === 'manual');
  const autoEntry = pairEntries.find((e) => e.source === 'auto');
  const effectiveEntry = manualEntry ?? autoEntry ?? null;
  // The dropdown shows the manual override if any, otherwise the auto-pinned
  // level with an "(auto)" hint, otherwise "off".
  const dropdownValue: TraceLevel | 'off' = effectiveEntry?.level ?? 'off';

  const handleLevelChange = (next: TraceLevel | 'off') => {
    if (!filters.pairId) return;
    if (next === 'off') {
      // Clear manual; auto remains if a rule is active.
      setManualDebug.mutate({ pairId: filters.pairId, level: null });
    } else {
      setManualDebug.mutate({
        pairId: filters.pairId,
        level: next,
        ttlMinutes: 60,
      });
    }
  };

  const handleClearConfirm = async () => {
    const scope = clearOpen?.scope;
    const pairId = scope === 'pair' ? filters.pairId ?? undefined : undefined;
    if (tab === 'traces') {
      await clearTraces.mutateAsync({ pairId });
    } else {
      await clearLogs.mutateAsync({ pairId });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 1 }}>
      {/* Header / sub-tabs */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab value="traces" label="Traces" />
          <Tab value="logs" label="Logs" />
        </Tabs>
        <Box sx={{ flex: 1 }} />

        {filters.pairId && (
          <Tooltip
            title={
              manualEntry
                ? `Manual override: ${manualEntry.level}${
                    manualEntry.expiresAt
                      ? ` until ${new Date(manualEntry.expiresAt).toLocaleTimeString()}`
                      : ''
                  }`
                : autoEntry
                ? `Auto-pinned to ${autoEntry.level} while rule is active. Pick another level to override (60 min).`
                : 'No override. Pick a level to set a 60-min manual override for this pair.'
            }
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <BugReportIcon
                fontSize="small"
                sx={{ color: manualEntry ? 'warning.main' : autoEntry ? 'info.main' : 'grey.500' }}
              />
              <Select
                size="small"
                value={dropdownValue}
                onChange={(e) => handleLevelChange(e.target.value as TraceLevel | 'off')}
                disabled={setManualDebug.isLoading}
                sx={{ minWidth: 100, fontSize: '0.75rem' }}
                renderValue={(v) => {
                  if (manualEntry) return `${v}`;
                  if (autoEntry) return `${v} (auto)`;
                  return 'off';
                }}
              >
                <MenuItem value="off">off</MenuItem>
                <MenuItem value="debug">debug</MenuItem>
                <MenuItem value="info">info</MenuItem>
                <MenuItem value="warn">warn</MenuItem>
                <MenuItem value="error">error</MenuItem>
              </Select>
            </Stack>
          </Tooltip>
        )}

        <Tooltip title="Clear for this market (requires pair filter)">
          <span>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={() => setClearOpen({ scope: 'pair' })}
              disabled={!filters.pairId}
            >
              Clear pair
            </Button>
          </span>
        </Tooltip>

        <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={() => setClearOpen({ scope: 'all' })}
        >
          Clear all
        </Button>
      </Stack>

      {/* Filters */}
      <DebugFilters
        value={filters}
        onChange={setFilters}
        liveTail={liveTail}
        onLiveTailChange={setLiveTail}
        showCategoryFilter={tab === 'traces'}
      />

      {/* Stats / drop warning */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ fontSize: '0.72rem', color: 'grey.500' }}>
        {stats && (
          <>
            <Typography variant="caption">
              traces: {stats.traces.count.toLocaleString()} stored, {stats.traces.queued} queued, {stats.traces.dropped} dropped
            </Typography>
            <Typography variant="caption">
              logs: {stats.logs.count.toLocaleString()} stored, {stats.logs.queued} queued, {stats.logs.dropped} dropped
            </Typography>
          </>
        )}
        {liveTail && (
          <Chip
            size="small"
            color={live.isConnected ? 'success' : 'default'}
            label={live.isConnected ? 'live' : 'connecting…'}
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        )}
        {(stats?.traces.dropped ?? 0) + (stats?.logs.dropped ?? 0) > 0 && (
          <Typography variant="caption" sx={{ color: 'warning.main' }}>
            ⚠ events dropped — write volume exceeded buffer
          </Typography>
        )}
      </Stack>

      {/* Content */}
      {tab === 'traces' ? (
        <TracesView query={traceQuery} liveEvents={live.traces} liveTail={liveTail} />
      ) : (
        <LogsView query={logQuery} liveEvents={live.logs} liveTail={liveTail} />
      )}

      <ClearDataDialog
        open={clearOpen !== null}
        onClose={() => setClearOpen(null)}
        kind={tab}
        pairName={clearOpen?.scope === 'pair' ? selectedPairName : null}
        onConfirm={handleClearConfirm}
        isLoading={clearTraces.isLoading || clearLogs.isLoading}
      />
    </Box>
  );
}
